import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import { MAX_DOWNLOAD_LIMIT } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { InvalidThreeMonth } from "../../../utils/misc";
import { reportsDownload } from "../../../utils/utils";
import MerchantSettlementListPage from "../../components/MerchantSettlementListPage";
import {
  useInitialMerchantSettlementFilterDataQuery,
  useMerchantSettlementQuery,
  useMerchantSettlementReportQuery
} from "../../queries";
import {
  merchantAddUrl,
  MerchantSettlementListUrl,
  MerchantSettlementListUrlDialog,
  MerchantSettlementListUrlQueryParams
} from "../../url";
import { downloadMerchantSettlementReport } from "../../util";
import { filterReportData, layoutSummary } from "../../util";
import {
  areFiltersApplied,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables
} from "./filters";

const MerchantSettlement = ({ params }) => {
  const initialState = { download: false, index: 0 };
  const [merchant, setMerchant] = useState<{
    download: boolean;
    index: number;
  }>(initialState);
  const notify = useNotifier();
  const navigate = useNavigator();
  const paginate = usePaginator();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);

  const currencySymbol = "PHP";

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const {
    data: initialFilterData
  } = useInitialMerchantSettlementFilterDataQuery({
    variables: {
      mall: params.mall
    }
  });

  const { data: previewData } = useMerchantSettlementQuery({
    displayLoader: true,
    skip: false,
    variables: previewVariables
  });

  const {
    data: merchantSettlementReport,
    loading: merchantSettlementReportLoading
  } = useMerchantSettlementReportQuery({
    displayLoader: true,
    skip: listElements?.length <= 0 || !merchant.download,
    variables: {
      payoutId: listElements[merchant.index]
    }
  });

  const searchBrand = useBrandSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: MerchantSettlementListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    MerchantSettlementListUrlDialog,
    MerchantSettlementListUrlQueryParams
  >(navigate, MerchantSettlementListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      MerchantSettlementListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.merchantSettlement.pageInfo,
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(
    params,
    {
      initial: maybe(
        () => initialFilterData.brands.edges.map(edge => edge.node) || []
      ),
      search: searchBrand
    },
    {
      initial: maybe(
        () => initialFilterData.malls.edges.map(edge => edge.node) || []
      ),
      search: searchMall
    }
  );

  useEffect(() => {
    if (InvalidThreeMonth(params.settlementDateFrom, params.settlementDateTo)) {
      notify({
        status: "error",
        text:
          "Unable to filter the report data. Please limit the date range to 3 months."
      });
      navigate(MerchantSettlementListUrl());
      reset();
    } else if (listElements.length > MAX_DOWNLOAD_LIMIT) {
      notify({
        status: "error",
        text:
          "You’ve reached the maximum number of settlements to be downloaded"
      });
      // removes last added element
      listElements.pop();
    } else {
      navigate(
        MerchantSettlementListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      );
    }
  }, [params.settlementDateFrom, listElements, settings.rowNumber]);

  const handleDownload = () => {
    setMerchant(prev => ({ ...prev, download: true }));
  };

  useEffect(() => {
    if (merchantSettlementReportLoading || !merchant.download) {
      return;
    }

    const dateNow = moment(new Date()).format("YYYYMMDDHHmmss");
    const orderDetailsFields =
      reportsDownload.merchantSettlementOrderDetails.field;

    const orderDetailsData = filterReportData(
      merchantSettlementReport.merchantSettlementReport.edges.map(
        edge => edge.node
      ),
      orderDetailsFields
    );

    const summaryData = layoutSummary(
      merchantSettlementReport?.merchantSettlementReport?.summary
    );

    const fileName = `${reportsDownload.merchantSettlementOrderDetails.filename}_${dateNow}.xlsx`;

    downloadMerchantSettlementReport(
      orderDetailsData,
      orderDetailsFields,
      fileName,
      summaryData
    );

    try {
      window.open("#", "_blank").close();
    } catch (error) {
      console.error(error.message);
    }

    if (merchant.index + 1 >= listElements.length) {
      notify({
        status: "success",
        text: `Successful download of ${listElements.length} Merchant Settlement Report/s`
      });
      closeModal();
      setMerchant(initialState);
    } else {
      setMerchant(prev => ({ ...prev, index: prev.index + 1 }));
    }
  }, [merchant, merchantSettlementReportLoading]);

  return (
    <>
      <MerchantSettlementListPage
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={""}
        onSearchChange={() => {
          throw new Error("Function not implemented.");
        }}
        onFilterChange={changeFilters}
        onAll={() => {
          resetFilters();
          reset();
        }}
        onTabChange={handleTabChange}
        tabs={tabs.map(tab => tab.name)}
        disabled={false}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        settlements={previewData?.merchantSettlement?.edges.map(
          edge => edge.node
        )}
        brands={[]}
        toolbar={<></>}
        onAdd={() => navigate(merchantAddUrl)}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={(items, selected) =>
          toggleAll(items, selected, MAX_DOWNLOAD_LIMIT)
        }
        onRowClick={() => {
          throw new Error("Function not implemented.");
        }}
        onTabDelete={() => {
          throw new Error("Function not implemented.");
        }}
        onTabSave={() => {
          throw new Error("Function not implemented.");
        }}
        download={() => openModal("download")}
        hasSelected={listElements.length > 0}
      />
      <ActionDialog
        open={params.action === "download"}
        confirmButtonState="default"
        onClose={closeModal}
        onConfirm={handleDownload}
        title="Download Settlements"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a merchant settlement report with {total} row(s). Do you want to continue?"
            values={{
              total: <strong>{listElements?.length}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};

export default MerchantSettlement;
